import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom';
import { getProducts, getCategories } from './services/productService';
import { getAboutUs } from './services/pageService';
import ProductDetail from './components/ProductDetail';
import './App.css';

const SITE_EMAIL = 'sales@lumenoutdoor.com';
const API_ORIGIN = process.env.REACT_APP_API_ORIGIN || '';

const languages = [
  { code: 'en', label: 'EN' },
  { code: 'zh', label: '中文' },
  { code: 'es', label: 'ES' },
  { code: 'de', label: 'DE' }
];

const copy = {
  en: {
    tagline: 'Garden, Outdoor & Pet Supply Partner',
    navProducts: 'Products',
    navAbout: 'About',
    navContact: 'Contact',
    search: 'Search products...',
    heroEyebrow: 'Global sourcing made easier',
    heroTitle: 'Reliable garden, outdoor and pet products for importers',
    heroText: 'We help wholesalers, retailers and brand owners source practical consumer products with steady quality, flexible packing and export-ready service.',
    heroPrimary: 'View Products',
    heroSecondary: 'Send Inquiry',
    trust: ['OEM / ODM ready', 'Mixed container support', 'Export documentation', 'Pre-shipment inspection'],
    metricProducts: 'Product lines',
    metricMarkets: 'Export markets',
    metricResponse: 'Inquiry response',
    allProducts: 'All Products',
    featured: 'Featured Products',
    featuredText: 'Browse current items from our catalog. Custom packaging, mixed containers and OEM requests are welcome.',
    resultCount: (count) => `${count} product lines available`,
    noProducts: 'No products available',
    viewDetails: 'View details',
    inquiryNow: 'Inquire now',
    categoriesTitle: 'Main Categories',
    categoriesText: 'Focused ranges for garden living, outdoor recreation and everyday pet care.',
    capabilityTitle: 'Built for B2B sourcing',
    capabilityText: 'From product selection to packing, inspection and shipment, our workflow is shaped around importer and retail program needs.',
    advantagesTitle: 'Why Work With Us',
    processTitle: 'Export Process',
    aboutTitle: 'About Lumen Outdoor',
    aboutFallback: 'Lumen Outdoor supplies garden, outdoor and pet products for global trade customers. We focus on practical designs, consistent quality control and responsive communication from sampling to shipment.',
    contactTitle: 'Ready to source your next product line?',
    contactText: 'Tell us your target item, quantity, packaging needs and destination market. Our team will reply with product suggestions and quotation details.',
    contactButton: 'Email Sales',
    contactEmailLabel: 'Sales email',
    loading: 'Loading...',
    categories: {
      Garden: 'Planters, garden tools, watering accessories and seasonal living products.',
      Outdoor: 'Camping, patio, storage, recreation and travel-ready outdoor items.',
      Pet: 'Pet feeding, grooming, toys, carriers and home care accessories.'
    },
    advantages: [
      'Multi-category sourcing for garden, outdoor and pet channels',
      'OEM packaging and private label support',
      'Sample coordination before bulk orders',
      'Export documents and shipment follow-up'
    ],
    process: ['Requirement', 'Quotation', 'Sample', 'Production', 'Inspection', 'Shipment']
  },
  zh: {
    tagline: '花园、户外、宠物用品外贸供应伙伴',
    navProducts: '产品中心',
    navAbout: '关于我们',
    navContact: '联系询盘',
    search: '搜索产品...',
    heroEyebrow: '让全球采购更高效',
    heroTitle: '面向进口商的 Garden、Outdoor、Pet 产品供应',
    heroText: '我们为批发商、零售商和品牌客户提供稳定品质、灵活包装、适合出口的花园、户外和宠物类消费品。',
    heroPrimary: '查看产品',
    heroSecondary: '发送询盘',
    trust: ['支持 OEM / ODM', '支持混柜出货', '出口单证配合', '出货前验货'],
    metricProducts: '产品系列',
    metricMarkets: '出口市场',
    metricResponse: '询盘响应',
    allProducts: '全部产品',
    featured: '精选产品',
    featuredText: '浏览当前产品目录。支持定制包装、混柜出货和 OEM 需求。',
    resultCount: (count) => `当前显示 ${count} 个产品系列`,
    noProducts: '暂无产品',
    viewDetails: '查看详情',
    inquiryNow: '立即询价',
    categoriesTitle: '主营分类',
    categoriesText: '聚焦花园生活、户外休闲和日常宠物护理用品。',
    capabilityTitle: '面向 B2B 采购场景',
    capabilityText: '从选品、包装、验货到装运，我们围绕进口商、零售项目和品牌客户的采购流程提供配合。',
    advantagesTitle: '我们的优势',
    processTitle: '出口流程',
    aboutTitle: '关于 Lumen Outdoor',
    aboutFallback: 'Lumen Outdoor 面向全球贸易客户供应花园、户外和宠物用品。我们重视实用设计、稳定品控，并在打样到出货的全过程保持快速沟通。',
    contactTitle: '准备开发下一条产品线？',
    contactText: '告诉我们目标产品、数量、包装要求和目的市场，我们会回复产品建议与报价信息。',
    contactButton: '邮件联系',
    contactEmailLabel: '销售邮箱',
    loading: '加载中...',
    categories: {
      Garden: '花盆园艺、园林工具、浇灌配件与季节性庭院用品。',
      Outdoor: '露营、庭院、收纳、休闲运动和便携户外用品。',
      Pet: '宠物喂食、美容清洁、玩具、外出包和家居护理配件。'
    },
    advantages: [
      '覆盖 garden、outdoor、pet 多品类采购',
      '支持 OEM 包装和自有品牌项目',
      '大货前样品确认与细节沟通',
      '出口文件、装运和售后跟进'
    ],
    process: ['需求沟通', '报价', '打样', '生产', '验货', '出运']
  },
  es: {
    tagline: 'Proveedor de jardín, exterior y mascotas',
    navProducts: 'Productos',
    navAbout: 'Nosotros',
    navContact: 'Contacto',
    search: 'Buscar productos...',
    heroEyebrow: 'Abastecimiento global más simple',
    heroTitle: 'Productos de jardín, exterior y mascotas para importadores',
    heroText: 'Ayudamos a mayoristas, minoristas y marcas a comprar productos prácticos con calidad estable, embalaje flexible y servicio de exportación.',
    heroPrimary: 'Ver productos',
    heroSecondary: 'Enviar consulta',
    trust: ['OEM / ODM disponible', 'Contenedor mixto', 'Documentos de exportación', 'Inspección previa'],
    metricProducts: 'Líneas',
    metricMarkets: 'Mercados',
    metricResponse: 'Respuesta',
    allProducts: 'Todos',
    featured: 'Productos destacados',
    featuredText: 'Revise nuestro catálogo actual. Aceptamos embalaje personalizado, contenedores mixtos y solicitudes OEM.',
    resultCount: (count) => `${count} líneas de producto disponibles`,
    noProducts: 'Sin productos',
    viewDetails: 'Ver detalles',
    inquiryNow: 'Consultar',
    categoriesTitle: 'Categorías principales',
    categoriesText: 'Rangos enfocados para jardín, vida exterior y cuidado de mascotas.',
    capabilityTitle: 'Preparado para compras B2B',
    capabilityText: 'Desde selección y embalaje hasta inspección y envío, nuestro flujo responde a programas de importadores y retail.',
    advantagesTitle: 'Por qué elegirnos',
    processTitle: 'Proceso de exportación',
    aboutTitle: 'Sobre Lumen Outdoor',
    aboutFallback: 'Lumen Outdoor suministra productos de jardín, exterior y mascotas para clientes comerciales globales, con calidad consistente y comunicación rápida.',
    contactTitle: '¿Listo para comprar una nueva línea?',
    contactText: 'Indique producto, cantidad, embalaje y mercado destino. Le responderemos con sugerencias y cotización.',
    contactButton: 'Enviar email',
    contactEmailLabel: 'Email de ventas',
    loading: 'Cargando...',
    categories: {
      Garden: 'Macetas, herramientas de jardín, riego y productos de temporada.',
      Outdoor: 'Camping, patio, almacenaje, recreación y artículos portátiles.',
      Pet: 'Comederos, aseo, juguetes, transportines y accesorios para mascotas.'
    },
    advantages: [
      'Abastecimiento multi-categoría',
      'Soporte para marca privada y OEM',
      'Coordinación de muestras antes del pedido',
      'Documentos de exportación y seguimiento'
    ],
    process: ['Requisito', 'Cotización', 'Muestra', 'Producción', 'Inspección', 'Envío']
  },
  de: {
    tagline: 'Partner für Garten-, Outdoor- und Haustierprodukte',
    navProducts: 'Produkte',
    navAbout: 'Über uns',
    navContact: 'Kontakt',
    search: 'Produkte suchen...',
    heroEyebrow: 'Globaler Einkauf, einfacher gesteuert',
    heroTitle: 'Garten-, Outdoor- und Haustierprodukte für Importeure',
    heroText: 'Wir unterstützen Großhändler, Händler und Marken mit praktischen Produkten, stabiler Qualität, flexibler Verpackung und exportfähigem Service.',
    heroPrimary: 'Produkte ansehen',
    heroSecondary: 'Anfrage senden',
    trust: ['OEM / ODM bereit', 'Mischcontainer', 'Exportdokumente', 'Vorversandprüfung'],
    metricProducts: 'Produktlinien',
    metricMarkets: 'Exportmärkte',
    metricResponse: 'Antwortzeit',
    allProducts: 'Alle Produkte',
    featured: 'Ausgewählte Produkte',
    featuredText: 'Sehen Sie aktuelle Artikel aus unserem Katalog. Individuelle Verpackung, Mischcontainer und OEM sind möglich.',
    resultCount: (count) => `${count} Produktlinien verfügbar`,
    noProducts: 'Keine Produkte',
    viewDetails: 'Details ansehen',
    inquiryNow: 'Anfragen',
    categoriesTitle: 'Hauptkategorien',
    categoriesText: 'Sortimente für Garten, Outdoor-Freizeit und tägliche Haustierpflege.',
    capabilityTitle: 'Für B2B-Beschaffung gebaut',
    capabilityText: 'Von Auswahl und Verpackung bis Prüfung und Versand ist unser Ablauf auf Importeure und Handelsprogramme ausgerichtet.',
    advantagesTitle: 'Ihre Vorteile',
    processTitle: 'Exportprozess',
    aboutTitle: 'Über Lumen Outdoor',
    aboutFallback: 'Lumen Outdoor liefert Garten-, Outdoor- und Haustierprodukte für internationale Handelskunden mit konstantem Qualitätsanspruch und schneller Kommunikation.',
    contactTitle: 'Bereit für Ihre nächste Produktlinie?',
    contactText: 'Nennen Sie Produkt, Menge, Verpackung und Zielmarkt. Wir senden passende Vorschläge und Angebotsdetails.',
    contactButton: 'E-Mail senden',
    contactEmailLabel: 'Vertriebs-E-Mail',
    loading: 'Wird geladen...',
    categories: {
      Garden: 'Pflanzgefäße, Gartenwerkzeuge, Bewässerung und saisonale Gartenartikel.',
      Outdoor: 'Camping, Terrasse, Aufbewahrung, Freizeit und transportable Outdoor-Artikel.',
      Pet: 'Fütterung, Pflege, Spielzeug, Transport und Zubehör für Haustiere.'
    },
    advantages: [
      'Beschaffung über mehrere Kategorien',
      'OEM-Verpackung und Private Label',
      'Musterabstimmung vor Serienproduktion',
      'Exportdokumente und Versandverfolgung'
    ],
    process: ['Anforderung', 'Angebot', 'Muster', 'Produktion', 'Prüfung', 'Versand']
  }
};

const defaultCategories = [
  { id: 'garden-default', Name: 'Garden' },
  { id: 'outdoor-default', Name: 'Outdoor' },
  { id: 'pet-default', Name: 'Pet' }
];

const sampleProducts = [
  {
    id: 'sample-garden',
    Name: 'Garden Planter & Tool Set',
    Description: 'Export-ready garden planters, hand tools and watering accessories for retail programs.',
    categories: [{ id: 'garden-default', Name: 'Garden' }]
  },
  {
    id: 'sample-outdoor',
    Name: 'Outdoor Camping Storage Series',
    Description: 'Foldable storage, camping and patio accessories for seasonal outdoor promotions.',
    categories: [{ id: 'outdoor-default', Name: 'Outdoor' }]
  },
  {
    id: 'sample-pet',
    Name: 'Pet Care Accessory Collection',
    Description: 'Daily pet feeding, grooming and travel accessories with custom packing options.',
    categories: [{ id: 'pet-default', Name: 'Pet' }]
  }
];

const productProfiles = {
  Garden: {
    en: [
      {
        name: 'Garden Planter & Watering Collection',
        description: 'Retail-ready planters, watering cans, hose accessories and compact gardening tools for seasonal garden programs. Available with custom color, barcode label and display packaging options.',
        specs: ['PP, galvanized steel and powder-coated metal options', 'Color box, hang tag or mail-order packaging', 'Suitable for garden centers, supermarkets and online retail']
      },
      {
        name: 'Patio Growing & Garden Care Series',
        description: 'A practical range for balcony growing, patio care and home gardening, covering planters, kneeling pads, pruning accessories and watering solutions.',
        specs: ['Lightweight retail SKUs for mixed containers', 'Logo printing and private label support', 'Sample confirmation before bulk production']
      }
    ],
    zh: [
      {
        name: '花园种植与浇灌用品系列',
        description: '适合零售渠道的花盆、浇水壶、水管配件和小型园艺工具，可配合季节性园艺促销项目。支持定制颜色、条码标签和展示包装。',
        specs: ['PP、镀锌钢、喷粉金属等材质方案', '彩盒、吊牌、电商包装可选', '适合园艺中心、超市和线上零售']
      }
    ],
    es: [
      {
        name: 'Colección de macetas y riego',
        description: 'Macetas, regaderas, accesorios de manguera y herramientas compactas para programas de jardín, con opciones de color, etiqueta y embalaje personalizado.',
        specs: ['Opciones en PP, acero galvanizado y metal pintado', 'Caja de color, etiqueta o embalaje e-commerce', 'Adecuado para garden centers y retail']
      }
    ],
    de: [
      {
        name: 'Pflanz- und Bewässerungssortiment',
        description: 'Pflanzgefäße, Gießkannen, Schlauchzubehör und kompakte Gartenwerkzeuge für saisonale Programme, mit Farb-, Label- und Verpackungsoptionen.',
        specs: ['PP, verzinkter Stahl und pulverbeschichtetes Metall', 'Farbkarton, Hangtag oder Versandverpackung', 'Für Gartencenter, Supermärkte und Onlinehandel']
      }
    ]
  },
  Outdoor: {
    en: [
      {
        name: 'Outdoor Camping & Storage Series',
        description: 'Foldable storage, patio organization, camping accessories and travel-friendly outdoor items designed for seasonal promotions and everyday recreation.',
        specs: ['Space-saving cartons for export loading', 'Assorted colors and retail packs available', 'Mixed container sourcing across outdoor categories']
      },
      {
        name: 'Patio Leisure & Travel Accessories',
        description: 'Portable outdoor accessories for backyard, camping, picnic and travel channels, with practical structures and easy retail presentation.',
        specs: ['Durable textile, plastic and metal combinations', 'Retail packaging and instruction sheet support', 'Flexible SKU mix for importer programs']
      }
    ],
    zh: [
      {
        name: '户外露营与收纳用品系列',
        description: '涵盖折叠收纳、庭院整理、露营配件和便携户外用品，适合季节性促销和日常户外休闲场景。',
        specs: ['节省装柜空间的出口包装', '支持多色和零售包装组合', '可按户外品类混柜采购']
      }
    ],
    es: [
      {
        name: 'Serie de camping y almacenaje exterior',
        description: 'Almacenaje plegable, organización de patio, accesorios de camping y artículos portátiles para promociones de temporada y ocio diario.',
        specs: ['Cartones optimizados para exportación', 'Colores surtidos y packs retail', 'Abastecimiento mixto en categorías outdoor']
      }
    ],
    de: [
      {
        name: 'Outdoor-Camping- und Aufbewahrungsserie',
        description: 'Faltbare Aufbewahrung, Terrassenorganisation, Campingzubehör und reisetaugliche Outdoorartikel für Saisonaktionen und Freizeit.',
        specs: ['Raumsparende Exportkartons', 'Sortierte Farben und Retail-Packs', 'Mischcontainer über Outdoor-Kategorien']
      }
    ]
  },
  Pet: {
    en: [
      {
        name: 'Pet Feeding, Grooming & Travel Collection',
        description: 'Daily pet bowls, grooming tools, toys, carriers and home care accessories for pet retail shelves and online assortment expansion.',
        specs: ['Food-contact material options on request', 'Blister, color box and e-commerce packaging', 'Private label support for pet channels']
      },
      {
        name: 'Everyday Pet Care Accessory Range',
        description: 'Practical accessories for feeding, cleaning, play and travel, selected for repeat purchase programs and compact shelf presentation.',
        specs: ['Small-volume SKUs for broad assortment plans', 'Color and logo customization available', 'Sample and packing review before order']
      }
    ],
    zh: [
      {
        name: '宠物喂食、美容与出行用品系列',
        description: '覆盖宠物碗、美容工具、玩具、外出包和家居护理配件，适合宠物零售货架和线上产品线扩充。',
        specs: ['可按需求沟通食品接触材质', '吸塑、彩盒、电商包装可选', '支持宠物渠道自有品牌项目']
      }
    ],
    es: [
      {
        name: 'Colección de alimentación, cuidado y viaje para mascotas',
        description: 'Comederos, herramientas de aseo, juguetes, transportines y accesorios para hogar, pensados para retail y surtidos online.',
        specs: ['Material apto para contacto alimentario bajo solicitud', 'Blíster, caja de color o embalaje e-commerce', 'Soporte para marca privada']
      }
    ],
    de: [
      {
        name: 'Fütterungs-, Pflege- und Reisesortiment für Haustiere',
        description: 'Näpfe, Pflegewerkzeuge, Spielzeug, Transporttaschen und Wohnaccessoires für Pet-Retail und Online-Sortimente.',
        specs: ['Lebensmittelkontakt-Materialien auf Anfrage', 'Blister, Farbkarton oder E-Commerce-Verpackung', 'Private-Label-Unterstützung']
      }
    ]
  }
};

const normalizeText = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value.map((block) => block.children?.map((child) => child.text).join('') || '').join('\n');
  }
  return '';
};

const getCategoryName = (category) => category?.Name || category?.name || '';

const buildAssetUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_ORIGIN}${url}`;
};

const getProductImageUrl = (product) => {
  if (product.Images?.[0]?.url) {
    return buildAssetUrl(product.Images[0].url);
  }
  return null;
};

function ProductImage({ product, productName }) {
  const [imageFailed, setImageFailed] = useState(false);
  const imageUrl = getProductImageUrl(product);

  if (!imageUrl || imageFailed) {
    return <div className="image-placeholder">{productName.slice(0, 2).toUpperCase()}</div>;
  }

  return <img src={imageUrl} alt={productName} loading="lazy" onError={() => setImageFailed(true)} />;
}

const isWeakProductText = (value) => {
  const text = normalizeText(value).trim();
  if (!text) return true;
  if (/^\d+[\d\sA-Za-z-]*$/.test(text)) return true;
  if (text.length < 18 && /\d/.test(text)) return true;
  return false;
};

const getProductCategory = (product) => getCategoryName(product.categories?.[0]) || 'Garden';

const enhanceProduct = (product, index, lang) => {
  const category = getProductCategory(product);
  const profiles = productProfiles[category]?.[lang] || productProfiles[category]?.en || productProfiles.Garden.en;
  const profile = profiles[index % profiles.length];
  const nameLooksTemporary = !product.Name || /\d/.test(product.Name) || /_ok|test|sample/i.test(product.Name);
  const descriptionLooksTemporary = isWeakProductText(product.Description);

  return {
    ...product,
    displayName: nameLooksTemporary ? profile.name : product.Name,
    displayDescription: descriptionLooksTemporary ? profile.description : normalizeText(product.Description),
    specs: profile.specs,
    categoryName: category
  };
};

function Header({ lang, setLang, searchTerm, onSearch, showSearch = true }) {
  const t = copy[lang];
  const navigate = useNavigate();

  return (
    <header className="site-header">
      <div className="header-content">
        <div className="logo" onClick={() => navigate('/')}>
          <h1>Lumen Outdoor</h1>
          <p>{t.tagline}</p>
        </div>

        <div className="header-right">
          <nav className="main-nav">
            <Link to="/" className="nav-link">{t.navProducts}</Link>
            <Link to="/about" className="nav-link">{t.navAbout}</Link>
            <a href="#contact" className="nav-link">{t.navContact}</a>
          </nav>

          {showSearch && (
            <div className="nav-search">
              <input type="text" placeholder={t.search} value={searchTerm} onChange={onSearch} />
            </div>
          )}

          <div className="language-switcher" aria-label="Language switcher">
            {languages.map((item) => (
              <button
                key={item.code}
                className={lang === item.code ? 'active' : ''}
                onClick={() => setLang(item.code)}
                type="button"
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}

function HomePage({ products, categories, lang, setLang }) {
  const t = copy[lang];
  const navigate = useNavigate();
  const sourceProducts = useMemo(
    () => (products.length ? products : sampleProducts).map((product, index) => enhanceProduct(product, index, lang)),
    [products, lang]
  );
  const sourceCategories = categories.length ? categories : defaultCategories;
  const [activeCategory, setActiveCategory] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');

  const displayProducts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return sourceProducts.filter((product) => {
      const matchesCategory = activeCategory === 'ALL' || product.categories?.some((cat) => cat.id === activeCategory);
      const haystack = `${product.displayName || product.Name || ''} ${product.displayDescription || normalizeText(product.Description)}`.toLowerCase();
      const matchesSearch = !term || haystack.includes(term);
      return matchesCategory && matchesSearch;
    });
  }, [sourceProducts, activeCategory, searchTerm]);

  const industryCategories = defaultCategories.map((item) => {
    const liveCategory = sourceCategories.find((cat) => getCategoryName(cat).toLowerCase() === item.Name.toLowerCase());
    return liveCategory || item;
  });

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  return (
    <>
      <Header lang={lang} setLang={setLang} searchTerm={searchTerm} onSearch={handleSearch} />

      <main>
        <section className="hero-section">
          <div className="hero-copy">
            <span className="eyebrow">{t.heroEyebrow}</span>
            <h2>{t.heroTitle}</h2>
            <p>{t.heroText}</p>
            <div className="hero-actions">
              <a href="#products" className="primary-action">{t.heroPrimary}</a>
              <a href={`mailto:${SITE_EMAIL}`} className="secondary-action">{t.heroSecondary}</a>
            </div>
            <div className="trust-strip">
              {t.trust.map((item) => <span key={item}>{item}</span>)}
            </div>
          </div>
          <div className="hero-panel">
            <div>
              <strong>60+</strong>
              <span>{t.metricProducts}</span>
            </div>
            <div>
              <strong>30+</strong>
              <span>{t.metricMarkets}</span>
            </div>
            <div>
              <strong>24h</strong>
              <span>{t.metricResponse}</span>
            </div>
          </div>
        </section>

        <div className="content-area">
          <section className="category-showcase">
            <div className="section-heading">
              <span>{t.categoriesTitle}</span>
              <h2>{t.categoriesText}</h2>
            </div>
            <div className="category-cards">
              {industryCategories.map((cat) => {
                const name = getCategoryName(cat);
                return (
                  <button
                    key={cat.id}
                    className={`category-card ${activeCategory === cat.id ? 'active' : ''}`}
                    onClick={() => {
                      setActiveCategory(cat.id);
                      setSearchTerm('');
                    }}
                    type="button"
                  >
                    <span>{name}</span>
                    <p>{t.categories[name] || t.categories.Garden}</p>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="capability-section">
            <div>
              <span>{t.capabilityTitle}</span>
              <h2>{t.capabilityText}</h2>
            </div>
            <div className="capability-grid">
              {t.trust.map((item) => <strong key={item}>{item}</strong>)}
            </div>
          </section>

          <section className="product-section" id="products">
            <div className="section-heading compact">
              <div>
                <span>{t.featured}</span>
                <h2>{t.featuredText}</h2>
              </div>
              <p className="result-count">{t.resultCount(displayProducts.length)}</p>
            </div>

            <div className="category-nav">
              <button className={activeCategory === 'ALL' ? 'active' : ''} onClick={() => setActiveCategory('ALL')} type="button">
                {t.allProducts}
              </button>
              {sourceCategories.map((cat) => (
                <button
                  key={cat.id}
                  className={activeCategory === cat.id ? 'active' : ''}
                  onClick={() => {
                    setActiveCategory(cat.id);
                    setSearchTerm('');
                  }}
                  type="button"
                >
                  {getCategoryName(cat)}
                </button>
              ))}
            </div>

            <div className="product-grid">
              {displayProducts.length === 0 ? (
                <p className="no-products">{t.noProducts}</p>
              ) : (
                displayProducts.map((product) => {
                  const productName = product.displayName || product.Name || 'Product';
                  const description = product.displayDescription || normalizeText(product.Description);
                  const subject = encodeURIComponent(`Inquiry for ${productName}`);

                  return (
                    <article
                      key={product.id}
                      className="product-card"
                      onClick={() => navigate(`/product/${product.id}`)}
                    >
                      <div className="product-image">
                        <ProductImage product={product} productName={productName} />
                      </div>
                      <div className="product-info">
                        <span className="product-category">{product.categoryName || getCategoryName(product.categories?.[0])}</span>
                        <h3>{productName}</h3>
                        <p>{description || t.categories[getCategoryName(product.categories?.[0])] || t.featuredText}</p>
                        <div className="product-meta">
                          {(product.specs || []).slice(0, 2).map((item) => <small key={item}>{item}</small>)}
                        </div>
                        <div className="product-actions">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(`/product/${product.id}`);
                            }}
                          >
                            {t.viewDetails}
                          </button>
                          <a href={`mailto:${SITE_EMAIL}?subject=${subject}`} onClick={(event) => event.stopPropagation()}>{t.inquiryNow}</a>
                        </div>
                      </div>
                    </article>
                  );
                })
              )}
            </div>
          </section>

          <section className="advantage-section">
            <div className="advantage-block">
              <span>{t.advantagesTitle}</span>
              <ul>
                {t.advantages.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>
            <div className="process-block">
              <span>{t.processTitle}</span>
              <div className="process-list">
                {t.process.map((item, index) => (
                  <div key={item}>
                    <strong>{String(index + 1).padStart(2, '0')}</strong>
                    <p>{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <ContactSection t={t} />
        </div>
      </main>
    </>
  );
}

function ContactSection({ t }) {
  return (
    <section className="contact-section" id="contact">
      <div>
        <span>{t.navContact}</span>
        <h2>{t.contactTitle}</h2>
        <p>{t.contactText}</p>
        <p className="contact-email"><strong>{t.contactEmailLabel}:</strong> <a href={`mailto:${SITE_EMAIL}`}>{SITE_EMAIL}</a></p>
      </div>
      <a href={`mailto:${SITE_EMAIL}`} className="primary-action">{t.contactButton}</a>
    </section>
  );
}

function AboutPage({ aboutContent, lang, setLang }) {
  const t = copy[lang];
  const body = normalizeText(aboutContent?.Content) || t.aboutFallback;
  const title = aboutContent?.Title || t.aboutTitle;
  const imageUrl = aboutContent?.Image?.url ? buildAssetUrl(aboutContent.Image.url) : null;

  return (
    <>
      <Header lang={lang} setLang={setLang} showSearch={false} />
      <main className="about-layout">
        <section className="about-page">
          <span className="eyebrow">{t.aboutTitle}</span>
          <h1>{title}</h1>
          {imageUrl && <img src={imageUrl} alt={title} className="about-image" />}
          <div className="about-content">{body}</div>
        </section>
        <ContactSection t={t} />
      </main>
    </>
  );
}

function App() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [aboutContent, setAboutContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState(() => localStorage.getItem('siteLanguage') || 'en');

  useEffect(() => {
    localStorage.setItem('siteLanguage', lang);
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [productsRes, categoriesRes, aboutRes] = await Promise.all([
        getProducts(),
        getCategories(),
        getAboutUs()
      ]);

      if (productsRes?.data) setProducts(productsRes.data);
      if (categoriesRes?.data) setCategories(categoriesRes.data);
      if (aboutRes?.data) setAboutContent(aboutRes.data);
    } catch (error) {
      console.error('Load failed:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading-state">{copy[lang].loading}</div>;
  }

  return (
    <Router>
      <Routes>
        <Route
          path="/"
          element={<HomePage products={products} categories={categories} lang={lang} setLang={setLang} />}
        />
        <Route path="/about" element={<AboutPage aboutContent={aboutContent} lang={lang} setLang={setLang} />} />
        <Route path="/product/:id" element={<ProductDetail products={(products.length ? products : sampleProducts).map((product, index) => enhanceProduct(product, index, lang))} lang={lang} copy={copy} siteEmail={SITE_EMAIL} />} />
      </Routes>
    </Router>
  );
}

export default App;
